import { useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import Moment from 'react-moment'
import momentDurationFormatSetup from 'moment-duration-format'
import moment, { ISO_8601 } from 'moment'
import { changeOptimalStartTask } from '../../flag-management/flag-management.services'
import { useHistory, useLocation } from 'react-router-dom'

import { Modal, Button } from 'antd'
import { WarningOutlined, ClockCircleOutlined } from '@ant-design/icons'
import { InfoCircleOutlined } from '@ant-design/icons'
import JobReschedule from '../../flag-management/job-detail/job-info/job-reschedule.component'
import { TimeInfoCard } from './time-info-card.component'
import { FeaturesConfigurationContext } from '../../../refactory/modules/jobs/infrastructure/job-detail/features-configuration.context'

const ChangeOptimalStart = ({ task, job, jobsSearch, setSelectedJob, timezone }) => {
  momentDurationFormatSetup(moment)
  const { t } = useTranslation()
  const location = useLocation()
  const history = useHistory()
  const { canUpdateTaskOptimals } = useContext(FeaturesConfigurationContext)
  const daysOfWeek = [
    t('jobs.tasks.changeOptimalStart.sunday'),
    t('jobs.tasks.changeOptimalStart.monday'),
    t('jobs.tasks.changeOptimalStart.tuesday'),
    t('jobs.tasks.changeOptimalStart.wednesday'),
    t('jobs.tasks.changeOptimalStart.thursday'),
    t('jobs.tasks.changeOptimalStart.friday'),
    t('jobs.tasks.changeOptimalStart.saturday')
  ]
  const formatDateToISO = (date) => {
    if (moment(date, ISO_8601, true).isValid()) {
      return date
    }
    if (date && typeof date === 'object' && date.hasOwnProperty('seconds') && date.hasOwnProperty('nanoseconds')) {
      const ISOFormattedDate = moment
        .unix(date.seconds)
        .add(date.nanoseconds / 1e9, 'seconds')
        .toISOString()
      return ISOFormattedDate
    }
    const ISOFormattedDate = moment(date).toISOString()

    return ISOFormattedDate
  }

  const [optimalStart, setOptimalStart] = useState('')
  const [optimalEnd, setOptimalEnd] = useState('')
  const [showJobReschedule, setShowJobReschedule] = useState(false)
  const stringDay = daysOfWeek[moment(optimalStart).day()]
  const optimalStartTime = moment(optimalStart).format('HH:mm')

  const [isEditOptimalsAvailable, setEditOptimalsAvailable] = useState(false)
  const [newOptimalTime, setNewOptimalTime] = useState(optimalStartTime)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    setOptimalStart(formatDateToISO(task.optimalStartTime))
    setOptimalEnd(formatDateToISO(task.optimalEndTime))
  }, [task])

  const getFormattedDurationTime = (startDate, endDate) => {
    let duration = 0
    const diff = moment(endDate).diff(moment(startDate), 'minutes')
    if (diff > 59) {
      duration = moment.duration(diff, 'minutes').format('h:mm [hrs]')
    } else {
      duration = moment.duration(diff, 'minutes').format('h:mm [min]')
    }
    return duration
  }

  const updateOptimalStartTime = async (task, newTime) => {
    const taskDate = moment(optimalStart).format('YYYY-MM-DD')
    const fullDate = moment(taskDate + 'T' + newTime)
      .utc()
      .format()
    const result = await changeOptimalStartTask(task.uuid, fullDate)
    setOptimalStart(result.optimal_start)
    setOptimalEnd(result.optimal_end)
    setEditOptimalsAvailable(false)
    setNewOptimalTime(newOptimalTime)
    setIsModalOpen(false)
  }

  const applyAndRescheduleOrder = async (task, newTime) => {
    await updateOptimalStartTime(task, newTime)
    setIsModalOpen(false)
    handleJobReschedule()
  }

  const handleJobReschedule = () => {
    if (!location.pathname.includes('reschedule')) {
      history.push(`${location.pathname}/reschedule`)
    } else if (location.pathname.includes('reschedule') && showJobReschedule) {
      history.replace(location.pathname.split('/reschedule')[0])
    }
    setShowJobReschedule(!showJobReschedule)
  }

  useEffect(() => {
    return () => {
      setEditOptimalsAvailable(false)
      setNewOptimalTime(optimalStartTime)
      setIsModalOpen(false)
      setOptimalStart('')
      setOptimalEnd('')
      setShowJobReschedule(false)
    }
  }, [])

  return (
    <>
      <article className='change-optimal-container'>
        <header>
          <h3>{t('jobs.tasks.changeOptimalStart.title')}</h3>
          <InfoCircleOutlined />
        </header>
        <main>
          <TimeInfoCard
            canUpdateTaskOptimals={canUpdateTaskOptimals}
            task={task}
            secondaryTimeToShow={<Moment format='hh:mm A, DD MMM' date={optimalStart} tz={timezone} />}
            primaryTimeToShow={
              <Moment format='hh:mm A, DD MMM YYYY' date={formatDateToISO(task.realStartTime)} tz={timezone} />
            }
            secondaryLabel='jobs.tasks.changeOptimalStart.optimalStart'
            primaryLabel='jobs.tasks.changeOptimalStart.start'
            isEditOptimalsAvailable={isEditOptimalsAvailable}
            setIsModalOpen={setIsModalOpen}
            newOptimalTime={newOptimalTime}
            setNewOptimalTime={setNewOptimalTime}
            setEditOptimalsAvailable={setEditOptimalsAvailable}
          />
          <TimeInfoCard
            task={task}
            secondaryTimeToShow={<Moment format='hh:mm A, DD MMM' date={optimalEnd} tz={timezone} />}
            primaryTimeToShow={
              <Moment format='hh:mm A, DD MMM YYYY' date={formatDateToISO(task.realEndTime)} tz={timezone} />
            }
            secondaryLabel='jobs.tasks.changeOptimalStart.optimalEnd'
            primaryLabel='jobs.tasks.changeOptimalStart.end'
            typeSecondary={true}
          />
          <TimeInfoCard
            task={task}
            secondaryTimeToShow={getFormattedDurationTime(optimalStart, optimalEnd)}
            primaryTimeToShow={getFormattedDurationTime(
              formatDateToISO(task.realStartTime),
              formatDateToISO(task.realEndTime)
            )}
            secondaryLabel='jobs.tasks.changeOptimalStart.estimatedDuration'
            primaryLabel='jobs.tasks.changeOptimalStart.duration'
            typeSecondary={true}
          />
        </main>
      </article>
      {showJobReschedule && (
        <JobReschedule
          jobId={task.jobId}
          handleJobReschedule={handleJobReschedule}
          visible={showJobReschedule}
          job={job}
          setSelectedJob={setSelectedJob}
          jobsSearch={jobsSearch}
        />
      )}
      <Modal
        visible={isModalOpen}
        mask={true}
        onCancel={() => setIsModalOpen(false)}
        maskStyle={{ zIndex: 10001000, background: 'rgba(0,0,0,0.45)' }}
        width={416}
        footer={null}
        closable={false}
        centered
        bodyStyle={{ display: 'flex', padding: '32px 32px 24px' }}
      >
        <WarningOutlined style={{ color: '#FAAD14', fontSize: '18px' }} />
        <article style={{ marginLeft: '17px' }}>
          <p style={{ fontSize: '16px', marginBottom: '8px', color: 'black' }}>
            <b>{t('jobs.tasks.changeOptimalStart.modal.title')}</b>
          </p>
          <p style={{ fontSize: '14px', marginBottom: '8px' }}>{t('jobs.tasks.changeOptimalStart.modal.slotOrder')}:</p>
          <div style={{ width: '270px', marginBottom: '24px', background: '#EAEDF3', borderRadius: '4px' }}>
            <ClockCircleOutlined style={{ color: '#1890FF', fontSize: '13.5px', marginLeft: '10px' }} />
            <span style={{ fontSize: '12px', marginLeft: '10px', color: 'black' }}>
              <Moment format='hh:mm A' date={optimalStart} /> {t('jobs.tasks.changeOptimalStart.modal.to')}{' '}
              <Moment format='hh:mm A' date={optimalEnd} />, {stringDay} <Moment format='DD MMM' date={optimalStart} />
            </span>
          </div>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
            <Button style={{ color: 'black' }} onClick={() => applyAndRescheduleOrder(task, newOptimalTime)}>
              {t('jobs.tasks.changeOptimalStart.modal.rescheduleOrder')}
            </Button>
            <Button
              type='primary'
              style={{ marginLeft: '10px' }}
              onClick={() => updateOptimalStartTime(task, newOptimalTime)}
            >
              {t('jobs.tasks.changeOptimalStart.modal.continue')}
            </Button>
          </div>
        </article>
      </Modal>
    </>
  )
}

export { ChangeOptimalStart }
